import {
	Alert,
	AlertDescription,
	AlertIcon,
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useToast,
	VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { InfoType, TemplateListType } from '../../../types/types';
import { MakeFormFieldsFromInfo } from '../../../utils/MakeFormFieldsFromInfo';
import { makeInitialValuesFromInfoAndDetails } from '../../../utils/makeInitialValuesFromInfo';

interface EditTemplateModalProps {
	template?: TemplateListType;
	isOpen: boolean;
	onClose: () => void;
	refetch: () => void;
}

export const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
	template,
	isOpen,
	onClose,
	refetch,
}) => {
	const [initialValues, setInitialValues] = useState<any>({});
	const [templateInfo, setTemplateInfo] = useState<InfoType[]>([]);
	const toast = useToast();
	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		onSubmit: async (values, { setStatus }) => {
			const { name, ...details } = values;
			try {
				await axios.put(
					`${process.env.REACT_APP_HBS_BASE_URL}/api/v1/template`,
					{
						name,
						_id: template?._id,
						details,
					}
				);
				toast({
					status: 'success',
					position: 'top',
					description: 'Template details updated',
				});
				refetch();
				closeFunction();
			} catch (err: any) {
				setStatus(err.response.data?.message || 'An error occurred');
			}
		},
	});
	const closeFunction = () => {
		formik.resetForm();
		formik.setStatus('');
		onClose();
	};

	useEffect(() => {
		const getData = async () => {
			try {
				const result = await axios.get(
					`${process.env.REACT_APP_HBS_BASE_URL}/api/v1/template/template-structure`
				);
				setTemplateInfo(result.data.data);
			} catch (err: any) {
				toast({
					position: 'top',
					status: 'error',
					description: err?.response?.data?.message || 'An error occurred',
				});
			}
		};
		getData();
	}, [toast]);

	useEffect(() => {
		if (template && templateInfo.length > 0) {
			let init = makeInitialValuesFromInfoAndDetails(templateInfo, template);
			setInitialValues({ ...init, name: template.name });
		}
	}, [template, templateInfo]);

	return (
		<>
			<Modal isOpen={isOpen} onClose={closeFunction}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Edit template</ModalHeader>
					<ModalCloseButton />
					<form onSubmit={formik.handleSubmit}>
						<ModalBody>
							{formik.status && (
								<Alert status='error' mb='2'>
									<AlertIcon />
									<AlertDescription>{formik.status}</AlertDescription>
								</Alert>
							)}
							<VStack spacing={4}>
								<FormControl
									id='name'
									isInvalid={formik.errors.name ? true : false}
									mb='4'
								>
									<FormLabel>Name of the page</FormLabel>
									<Input type='text' {...formik.getFieldProps('name')} />
									<FormErrorMessage>{formik.errors.name}</FormErrorMessage>
								</FormControl>
								{templateInfo.length > 0 &&
									template &&
									templateInfo.map((info) => {
										return (
											<MakeFormFieldsFromInfo
												field={info}
												formik={formik}
												key={info.name}
											/>
										);
									})}
							</VStack>
						</ModalBody>

						<ModalFooter justifyContent='center'>
							<Button
								colorScheme='green'
								type='submit'
								mr={3}
								isLoading={formik.isSubmitting}
							>
								Submit
							</Button>
							<Button colorScheme='gray' onClick={closeFunction}>
								Close
							</Button>
						</ModalFooter>
					</form>
				</ModalContent>
			</Modal>
		</>
	);
};
